import 'locales/i18n';
import * as uuid from 'uuid';
import 'simplebar/src/simplebar.css';
import 'react-18-image-lightbox/style.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'assetClass/presentation/map/openlayers/openlayers.css';
import 'ol/ol.css';

import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { store, persistor } from 'redux/store';
import { AuthProvider } from 'identityAndAccess/infrastructure/AwsCognitoJwtContext';
import { LogProvider } from 'common/infrastructure/log/LogContext';
import { OnlineStatusProvider } from 'common/infrastructure/network/onlineStatus/OnlineStatusContext';
import RemoteStateProvider from 'common/infrastructure/remoteState/persistQueryClient';
import CacheBuster from 'common/infrastructure/cacheBuster/CacheBuster';
import App from 'App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { setNodeId } from '@planitgeo/treeplotter-domain/lib/infrastructure/clock/hybrid-logical-clock';
import { init as initHandlers } from 'common/application/handlers';
import { SettingsProvider } from 'common/infrastructure/settings/SettingsContext';
import { detectiOS } from 'utils/platformDetection';
import { installReloadOnStaleChunk } from 'utils/reloadOnStaleChunk';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';

/**
 * iOS Safari auto-zooms the page when an input under 16px receives focus and
 * never zooms back out. Appending maximum-scale=1 to the viewport suppresses
 * that automatic zoom.
 */
const preventIOSInputFocusZoom = () => {
  if (!detectiOS()) return;

  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) return;

  const content = viewport.getAttribute('content') ?? '';
  if (content.includes('maximum-scale')) return;

  viewport.setAttribute('content', `${content}, maximum-scale=1`);
};

preventIOSInputFocusZoom();

installReloadOnStaleChunk({
  target: window,
  // Read lazily: touching sessionStorage itself throws when storage is blocked, and the
  // installer only guards calls made through these methods.
  storage: {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
  },
  isOnline: () => navigator.onLine,
  reload: () => window.location.reload(),
  log: (message) => loggerService.log(LoggerLevel.WARN, message),
  // The hashed entry script differs on every deploy, so it identifies the build this page loaded.
  currentBuild: () =>
    document
      .querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
      ?.getAttribute('src') ?? 'unknown-build',
});

// Set the node id for the hybrid logical clock
const nodeId = localStorage.getItem('nodeId') || uuid.v4();
setNodeId(nodeId);
localStorage.setItem('nodeId', nodeId);

initHandlers();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <RemoteStateProvider>
    <ReduxProvider store={store}>
      <AuthProvider>
        <LogProvider>
          <OnlineStatusProvider>
            <CacheBuster>
              {({ shouldRenderApp }) => {
                // CacheBuster now handles version mismatch detection and cache clearing internally
                // If version mismatch is detected, it will clear cache and reload automatically
                // Loading state is handled internally by CacheBuster (shows LoadingScreen)
                if (!shouldRenderApp) return null;
                return (
                  <HelmetProvider>
                    <PersistGate loading={null} persistor={persistor}>
                      <SettingsProvider>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <BrowserRouter>
                            <App />
                          </BrowserRouter>
                        </LocalizationProvider>
                      </SettingsProvider>
                    </PersistGate>
                  </HelmetProvider>
                );
              }}
            </CacheBuster>
          </OnlineStatusProvider>
        </LogProvider>
      </AuthProvider>
    </ReduxProvider>
  </RemoteStateProvider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    console.log('[SW] New service worker available and activating automatically');
  },
  onSuccess: (registration) => {
    console.log('[SW] Service worker registered successfully');
  },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
